/**
 * Module-level cache for AI endpoints (shortlist + compare page).
 * Survives remounts so navigation / loading flicker does not re-bill xAI
 * for the same request.
 */

const DEFAULT_TTL_MS = 60 * 60 * 1000; // 1 hour

const stores = {
  quickComparison: new Map(),
  shortlistCoach: new Map(),
  comparisonSummary: new Map(),
};

const inFlight = {
  quickComparison: new Map(),
  shortlistCoach: new Map(),
  comparisonSummary: new Map(),
};

function getStore(namespace) {
  return stores[namespace] || null;
}

function getInFlight(namespace) {
  return inFlight[namespace] || null;
}

export function buildShortlistAiCacheKey(productKey, searchParamsKey) {
  return `${productKey || ""}::${searchParamsKey || ""}`;
}

export function buildComparisonSummaryCacheKey({
  productKey,
  sessionKey,
  tableKey,
  topMatchId,
  valuePickId,
}) {
  return [
    productKey || "",
    sessionKey || "",
    tableKey || "",
    topMatchId || "",
    valuePickId || "",
  ].join("::");
}

/** @returns {undefined|*} undefined = miss; otherwise cached payload (may be null) */
export function readShortlistAiCache(namespace, key, ttlMs = DEFAULT_TTL_MS) {
  const store = getStore(namespace);
  if (!store || !key) return undefined;
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.ts > ttlMs) {
    store.delete(key);
    return undefined;
  }
  return entry.data;
}

export function writeShortlistAiCache(namespace, key, data) {
  const store = getStore(namespace);
  if (!store || !key) return;
  store.set(key, { ts: Date.now(), data });
}

/**
 * Deduped fetch: share one in-flight promise across remounts, then cache.
 */
export async function fetchShortlistAiCached({
  namespace,
  key,
  fetcher,
  ttlMs = DEFAULT_TTL_MS,
}) {
  const cached = readShortlistAiCache(namespace, key, ttlMs);
  if (cached !== undefined) {
    return { data: cached, fromCache: true };
  }

  const flight = getInFlight(namespace);
  if (flight?.has(key)) {
    const data = await flight.get(key);
    return { data, fromCache: true };
  }

  const promise = (async () => {
    const data = await fetcher();
    writeShortlistAiCache(namespace, key, data);
    return data;
  })();

  flight?.set(key, promise);
  try {
    const data = await promise;
    return { data, fromCache: false };
  } finally {
    flight?.delete(key);
  }
}
